import { Comment } from '@/types/comment';
import Avatar from '@/components/misc/Avatar';
import { format } from 'date-fns';
import { useTranslations, useLocale } from 'next-intl';
import { sv, enGB } from 'date-fns/locale';
import { useSession } from 'next-auth/react';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useCalendarStore } from '@/hooks/use-calendar-store';

function parseMentions(text: string) {
  const mentionRegex = /@\[([^\]]+)\]\(user:([a-f0-9-]+)\)/g;
  const parts: (string | { type: 'mention'; name: string; userId: string })[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    parts.push({ type: 'mention', name: match[1], userId: match[2] });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

export function CommentItem({
  comment,
  position = 'left',
  onDelete,
}: {
  comment: Comment;
  position?: 'left' | 'right';
  onDelete?: (commentId: string) => Promise<void>;
}) {
  const t = useTranslations('calendar');
  const locale = useLocale();
  const dateLocale = locale === 'sv' ? sv : enGB;
  const { data: session } = useSession();
  const { editEventObject, setEditEventObject } = useCalendarStore();
  const [isDeleting, setIsDeleting] = useState(false);
  const [shouldHighlight, setShouldHighlight] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isActive = editEventObject?.activeCommentId === comment.id;

  useEffect(() => {
    if (isActive && containerRef.current) {
      setShouldHighlight(true);

      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      const timer = setTimeout(() => {
        setShouldHighlight(false);

        if (editEventObject) {
          setEditEventObject({
            ...editEventObject,
            activeCommentId: undefined,
          });
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isActive, editEventObject, setEditEventObject]);

  const isOwnComment = session?.user?.id === comment.user.id;

  const handleDelete = async () => {
    if (!onDelete || !isOwnComment) return;

    // Simple confirmation
    if (!window.confirm(t('comments.confirmDelete'))) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete(comment.id);
    } catch (error) {
      console.error('Failed to delete comment:', error);
      // You might want to show a toast notification here
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`flex flex-row gap-2 ${position === 'right' ? 'justify-end pl-10' : 'pr-10'} ${
        shouldHighlight ? 'animate-highlight' : ''
      }`}
      style={{
        animation: shouldHighlight ? 'highlight 2s ease-out' : undefined,
      }}
    >
      <div>{position === 'left' && <Avatar name={comment.user.name} />}</div>
      <div className="flex flex-col">
        <div className="flex h-[32px] flex-row items-center justify-between gap-2">
          <div className="h-[22px] max-w-[50%] overflow-hidden text-ellipsis text-sm font-medium text-gray-700">
            {position === 'left' ? comment.user.name : t('comments.you')}
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-500">
              {format(comment.createdAt, 'PPp', { locale: dateLocale })}
            </div>
            {isOwnComment && onDelete && (
              <Button
                className="h-6 w-6 border-none bg-transparent p-0 text-gray-400 shadow-none hover:bg-red-50 hover:text-red-500"
                onClick={handleDelete}
                disabled={isDeleting}
                title={t('comments.delete')}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
        <div
          className={`border-1 border border-solid ${shouldHighlight ? 'border-blue-400' : 'border-gray-200'} ${position === 'right' ? 'rounded-tr-none' : 'rounded-tl-none'} rounded-md px-3 py-2 text-sm ${position === 'left' ? (shouldHighlight ? 'bg-blue-50' : 'bg-gray-50') : shouldHighlight ? 'bg-blue-50' : 'bg-white'} ${isDeleting ? 'opacity-50' : ''} transition-colors duration-300`}
        >
          {parseMentions(comment.comment).map((part, index) =>
            typeof part === 'string' ? (
              <span key={index}>{part}</span>
            ) : (
              <span
                key={index}
                className="rounded bg-blue-100 px-1 py-0.5 text-xs font-medium text-blue-800"
              >
                @{part.name}
              </span>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
